import React, { useState, useEffect } from 'react';
import { useApi, Spinner, ErrBanner, fmtNum, today } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import Can from '../../components/Can';
import './Master.css';

const TABS = ['Products', 'Tanks', 'Pumps', 'Nozzles', 'Customers', 'Suppliers'];

/* ═══════════ TANK LEDGER — transaction-level, below the Tank Master table ═══════════
   System-generated & read-only. Built from source tables by tank_id (purchases =
   Credit/Stock In; metered dispensing/testing/evap = Debit/Stock Out).
   Running Balance = Opening + Purchases − Sales − Testing − Evaporation.
   Variance (Physical Dip − Closing) is a separate field, not in the balance. */
const TANK_TXN_TYPES = ['All', 'PURCHASE', 'SALE', 'TESTING', 'EVAP'];

function TankLedger({ tanks }) {
  const tankList = tanks || [];
  const [tankId, setTankId] = useState('');
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [type, setType] = useState('All');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Default to the first tank once the list arrives.
  useEffect(() => {
    if (!tankId && tankList.length) setTankId(String(tankList[0].id));
  }, [tankList, tankId]);

  useEffect(() => {
    if (!tankId) return;
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await api.stockLedger.tankLedger(tankId, from, to, type === 'All' ? '' : type);
        if (alive) setData(res);
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load tank ledger');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tankId, from, to, type]);

  // `get()` already unwraps the {success,data} envelope, so `data` IS the
  // payload object. Be tolerant of both shapes the backend may return:
  //   • flat:   { rows, opening_balance, total_debit, total_credit, closing_balance, variance, physical_dip }
  //   • nested: { rows, totals: { opening|opening_balance, debit|total_debit,
  //               credit|total_credit, closing|closing_balance, variance } }
  const d = data || {};
  const t = d.totals || d;
  const num = (v) => Number(v ?? 0) || 0;
  const opening     = num(d.opening_balance ?? t.opening_balance ?? t.opening);
  const totalDebit  = num(d.total_debit ?? t.total_debit ?? t.debit ?? t.total_sales);
  const totalCredit = num(d.total_credit ?? t.total_credit ?? t.credit ?? t.purchases);
  const closing     = num(d.closing_balance ?? t.closing_balance ?? t.closing);
  const variance    = d.variance ?? t.variance ?? null;
  const physicalDip = d.physical_dip ?? t.physical_dip ?? null;
  const rows = (d.rows || []).map(r => ({
    date:            r.date ?? r.entry_date ?? r.txn_date ?? '',
    reference_no:    r.reference_no ?? r.ref_no ?? r.reference ?? '—',
    type:            r.type ?? r.transaction_type ?? r.txn_type ?? '',
    debit_qty:       num(r.debit_qty ?? r.debit),
    credit_qty:      num(r.credit_qty ?? r.credit),
    running_balance: num(r.running_balance ?? r.running ?? r.balance),
    remarks:         r.remarks ?? r.remark ?? r.narration ?? '',
  }));

  // Type filter is applied CLIENT-SIDE so it's instant and works regardless of
  // whether the backend honours the `type` param. Running balance / totals stay
  // computed over the full period (Closing must equal the true tank stock), so
  // only the visible rows change — the summary numbers intentionally do not.
  const TYPE_FILTER = { PURCHASE: 'PURCHASE', SALE: 'SALE', TESTING: 'TESTING LOSS', EVAP: 'EVAP LOSS' };
  const displayRows = type === 'All' ? rows : rows.filter(r => r.type === TYPE_FILTER[type]);

  return (
    <div className="section-card" style={{ marginTop: 16 }}>
      <div className="section-header">Tank Ledger</div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: 12, alignItems: 'flex-end' }}>
        <div className="form-group">
          <label>Tank</label>
          <select className="form-control" value={tankId} onChange={e => setTankId(e.target.value)}>
            {tankList.length === 0 && <option value="">No tanks</option>}
            {tankList.map(t => <option key={t.id} value={t.id}>{t.tank_no} ({t.product_code})</option>)}
          </select>
        </div>
        <div className="form-group"><label>From</label><input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div className="form-group"><label>To</label><input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} /></div>
        <div className="form-group">
          <label>Transaction Type</label>
          <select className="form-control" value={type} onChange={e => setType(e.target.value)}>
            {TANK_TXN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      {error && <ErrBanner msg={error} />}
      {loading ? <Spinner /> : (
        <>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Date</th>
                  <th style={{ textAlign: 'left' }}>Reference No</th>
                  <th style={{ textAlign: 'left' }}>Transaction Type</th>
                  <th style={{ textAlign: 'right' }}>Debit Qty</th>
                  <th style={{ textAlign: 'right' }}>Credit Qty</th>
                  <th style={{ textAlign: 'right' }}>Running Balance</th>
                  <th style={{ textAlign: 'left' }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ fontWeight: 600, background: '#f5f9fc' }}>
                  <td colSpan={5}>Opening Balance</td>
                  <td className="right-align">{fmtNum(opening)}</td>
                  <td></td>
                </tr>
                {displayRows.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No movements in this range</td></tr>
                ) : displayRows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.date}</td>
                    <td>{r.reference_no}</td>
                    <td><span className={`badge ${Number(r.credit_qty) > 0 ? 'badge-success' : 'badge-danger'}`}>{r.type}</span></td>
                    <td className="right-align">{Number(r.debit_qty) > 0 ? fmtNum(r.debit_qty) : '—'}</td>
                    <td className="right-align">{Number(r.credit_qty) > 0 ? fmtNum(r.credit_qty) : '—'}</td>
                    <td className="right-align" style={{ fontWeight: 700 }}>{fmtNum(r.running_balance)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.remarks}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, background: '#f5f9fc' }}>
                  <td colSpan={3} style={{ textAlign: 'right' }}>Totals:</td>
                  <td className="right-align">{fmtNum(totalDebit)}</td>
                  <td className="right-align">{fmtNum(totalCredit)}</td>
                  <td className="right-align">{fmtNum(closing)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '10px 14px', fontSize: 13 }}>
            <span><strong>Opening Balance:</strong> {fmtNum(opening)} L</span>
            <span><strong>Total Debit:</strong> {fmtNum(totalDebit)} L</span>
            <span><strong>Total Credit:</strong> {fmtNum(totalCredit)} L</span>
            <span><strong>Closing Balance:</strong> {fmtNum(closing)} L</span>
            <span style={{ color: variance == null ? 'var(--text-muted)' : (Number(variance) === 0 ? 'var(--secondary)' : 'var(--danger)') }}>
              <strong>Variance:</strong>{' '}
              {variance == null
                ? '— (no physical dip on record)'
                : `${fmtNum(variance)} L${physicalDip != null ? ` (Physical dip ${fmtNum(physicalDip)} − Closing ${fmtNum(closing)})` : ''}`}
            </span>
          </div>
          <div style={{ padding: '0 14px 12px', fontSize: 11, color: 'var(--text-muted)' }}>
            System-generated & read-only. Running Balance = Opening + Purchases − Sales − Testing − Evaporation.
            Variance is a separate reconciliation field (Physical Dip − Closing) and is not part of the balance.
          </div>
        </>
      )}
    </div>
  );
}

const EMPTY_P = { code: '', name: '', hsn_code: '', unit: 'Ltr', tax_per: 0, rate: 0, type: 'Lubricant' };
const EMPTY_T = { tank_no: '', product_code: 'MS', capacity: '', current_stock: '', min_level: '' };
const EMPTY_PU = { pump_no: '', name: '', product_code: '', nozzles: 2, status: 'Active', last_reading: 0 };

const EMPTY_NO = { nozzle_no: '', pump_id: '', tank_id: '' };
const EMPTY_C = { name: '', gstin: '', credit_limit: 0, balance: 0, type: 'Cash', phone: '', address: '' };
const EMPTY_S = { name: '', gstin: '', contact: '', product: '' };

export default function Master({ subPage = "Products" }) {
  const [tab, setTab] = useState(subPage);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [confirm, setConfirm] = useState(null);
  useEffect(() => { setTab(subPage); setShowForm(false); setEditRow(null); }, [subPage]);
  const [msg, setMsg] = useState('');

  const products = useApi(() => api.products.list(), []);
  const tanks = useApi(() => api.tanks.list(true), []);
  const pumps = useApi(() => api.pumps.list(), []);
  const nozzles = useApi(() => api.nozzles.list(true), []);
  const customers = useApi(() => api.customers.list(), []);
  const suppliers = useApi(() => api.suppliers.list(), []);
  const [employees, setEmployees] = useState([]);
  const [historyCode, setHistoryCode] = useState(null);
  const [confirmDeleteRate, setConfirmDeleteRate] = useState(null);
  const [confirmNozzle, setConfirmNozzle] = useState(null);

  const [pForm, setPForm] = useState(EMPTY_P);
  const [tForm, setTForm] = useState(EMPTY_T);
  const [puForm, setPuForm] = useState(EMPTY_PU);
  const [noForm, setNoForm] = useState(EMPTY_NO);
  const [cForm, setCForm] = useState(EMPTY_C);
  const [sForm, setSForm] = useState(EMPTY_S);

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  /* ── Products ── */
  const saveProduct = async () => {
    try {
      const res = await api.products.create(pForm);
      if (pForm.rate > 0) {
        await api.productRates.create({
          product_code: res.code || pForm.code,
          effective_date: new Date().toISOString().split('T')[0],
          rate: pForm.rate,
          tax_per: pForm.tax_per,
          remarks: 'Initial rate from Product Master'
        });
      }
      notify('✅ Product saved');
      setPForm(EMPTY_P);
      setShowForm(false);
      products.reload();
    }
    catch (e) { notify('❌ ' + e.message); }
  };
  const updateProduct = async () => {
    try {
      await api.products.update(editRow.id, editRow);
      // Also create a rate history entry if rate is provided
      if (editRow.rate !== undefined && editRow.rate !== '' && parseFloat(editRow.rate) > 0) {
        await api.productRates.create({
          product_code: editRow.code,
          effective_date: editRow.effective_date || new Date().toISOString().split('T')[0],
          rate: editRow.rate,
          tax_per: editRow.tax_per,
          remarks: editRow.remarks || 'Updated from Product Master'
        });
      }
      notify('✅ Product updated');
      setEditRow(null);
      products.reload();
    }
    catch (e) { notify('❌ ' + e.message); }
  };
  const delProduct = async () => {
    try { await api.products.remove(confirm.row.id); notify('✅ Deleted'); products.reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  /* ── Tanks ── */
  const saveTank = async () => {
    try { await api.tanks.create(tForm); notify('✅ Tank saved'); setTForm(EMPTY_T); setShowForm(false); tanks.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const updateTank = async () => {
    try { await api.tanks.update(editRow.id, editRow); notify('✅ Tank updated'); setEditRow(null); tanks.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const delTank = async () => {
    try { await api.tanks.remove(confirm.row.id); notify('✅ Deleted'); tanks.reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  /* ── Pumps ── */
  const savePump = async () => {
    if (!puForm.name || !puForm.product_code) { notify('❌ Name and Product required'); return; }
    try { await api.pumps.create({ ...puForm, nozzles: parseInt(puForm.nozzles) }); notify('✅ Pump added'); setPuForm(EMPTY_PU); setShowForm(false); pumps.reload(); }

    catch (e) { notify('❌ ' + e.message); }
  };
  const updatePump = async () => {
    try {
      await api.pumps.update(editRow.id, {
        name: editRow.name,
        product_code: editRow.product_code,
        nozzles: parseInt(editRow.nozzles),
        status: editRow.status,
        last_reading: parseFloat(editRow.last_reading || 0)
      });

      notify('✅ Pump updated'); setEditRow(null); pumps.reload();
    } catch (e) { notify('❌ ' + e.message); }
  };
  const delPump = async () => {
    try { await api.pumps.remove(confirm.row.id); notify('✅ Pump deleted'); pumps.reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };
  const togglePumpStatus = async (pump) => {
    const newStatus = pump.status === 'Active' ? 'Inactive' : 'Active';
    try {
      await api.pumps.toggleStatus(pump.id, newStatus);
      pumps.reload();
      notify('✅ Status updated');
    } catch (e) { notify('❌ ' + e.message); }
  };

  const toggleTankStatus = async (t) => {
    try {
      await api.tanks.toggleStatus(t.id, Number(t.is_active) ? 0 : 1);
      tanks.reload();
      notify('✅ Status updated');
    } catch (e) { notify('❌ ' + e.message); }
  };

  const toggleNozzleStatus = async (n) => {
    try {
      await api.nozzles.toggleStatus(n.id, Number(n.is_active) ? 0 : 1);
      nozzles.reload();
      notify('✅ Status updated');
    } catch (e) { notify('❌ ' + e.message); }
  };

  /* ── Nozzles ── */
  const saveNozzle = async () => {
    try {
      if (editRow && tab === 'Nozzles') {
        await api.nozzles.update(editRow.id, noForm);
        notify('✅ Nozzle updated');
        setEditRow(null);
      } else {
        await api.nozzles.create(noForm);
        notify('✅ Nozzle created');
      }
      setNoForm(EMPTY_NO); setShowForm(false); nozzles.reload();
    } catch (e) { notify('❌ ' + e.message); }
  };
  const delNozzle = async () => {
    try {
      await api.nozzles.remove(confirmNozzle.id);
      notify('✅ Nozzle deleted'); nozzles.reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirmNozzle(null);
  };

  /* ── Customers ── */
  const saveCustomer = async () => {
    try { await api.customers.create(cForm); notify('✅ Customer saved'); setCForm(EMPTY_C); setShowForm(false); customers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const updateCustomer = async () => {
    try { await api.customers.update(editRow.id, editRow); notify('✅ Customer updated'); setEditRow(null); customers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const delCustomer = async () => {
    try { await api.customers.remove(confirm.row.id); notify('✅ Deleted'); customers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  /* ── Suppliers ── */
  const saveSupplier = async () => {
    try { await api.suppliers.create(sForm); notify('✅ Supplier saved'); setSForm(EMPTY_S); setShowForm(false); suppliers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const updateSupplier = async () => {
    try { await api.suppliers.update(editRow.id, editRow); notify('✅ Supplier updated'); setEditRow(null); suppliers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
  };
  const delSupplier = async () => {
    try { await api.suppliers.remove(confirm.row.id); notify('✅ Deleted'); suppliers.reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const handleConfirmDelete = () => {
    if (!confirm) return;
    const map = { Products: delProduct, Tanks: delTank, Pumps: delPump, Customers: delCustomer, Suppliers: delSupplier };
    (map[confirm.entity] || (() => setConfirm(null)))();
  };

  const current = {
    Products: products, Tanks: tanks, Pumps: pumps, Nozzles: nozzles, Customers: customers, Suppliers: suppliers,
    Employees: { loading: false, error: null, data: employees }
  }[tab] || { loading: false, error: null, data: [] };

  const openEdit = (row) => {
    if (tab === 'Nozzles') {
      setNoForm({ nozzle_no: row.nozzle_no, pump_id: row.pump_id, tank_id: row.tank_id });
      setEditRow(row);
      setShowForm(true);
    } else {
      setEditRow({
        ...row,
        effective_date: new Date().toISOString().split('T')[0],
        remarks: ''
      });
      setShowForm(false);
    }
  };
  const openDel = (row, entity) => setConfirm({ row, entity });

  return (
    <div className="master-page">
      <div className="page-header">
        <h2 className="page-title">Master</h2>
        <Can
          module={
            tab === 'Products' ? 'products' :
              tab === 'Tanks' ? 'tanks' :
                tab === 'Pumps' ? 'pumps' :
                  tab === 'Nozzles' ? 'pumps' :
                    tab === 'Customers' ? 'customers' :
                      'suppliers'
          }
          action="create"
        >
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setShowForm(!showForm);
              setEditRow(null);
            }}
          >
            ＋ Add New
          </button>
        </Can>
      </div>
      {msg && <div style={{ padding: '6px 12px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>{msg}</div>}

      <div className="master-tabs">
        {TABS.map(t => (
          <button key={t} className={`master-tab ${tab === t ? 'active' : ''}`}
            onClick={() => {
              setTab(t);
              setShowForm(false);
              setEditRow(null);
              setNoForm(EMPTY_NO);
              window.location.hash = t; // Sync URL
            }}>
            {t === 'Products' && '🛢️ '}{t === 'Tanks' && '🔵 '}{t === 'Pumps' && '⛽ '}{t === 'Nozzles' && '🔧 '}{t === 'Customers' && '👤 '}{t === 'Suppliers' && '🏭 '}{t === 'Employees' && '👷 '}{t}
          </button>
        ))}
      </div>

      {current.loading && <Spinner />}
      {current.error && <ErrBanner msg={current.error} />}

      {/* ════════════ ADD FORMS ════════════ */}
      {showForm && tab === 'Products' && (
        <div className="section-card">
          <div className="section-header">Add Product</div>
          <div className="form-row">
            <div className="form-group"><label>Code (Leave blank to auto-generate)</label><input className="form-control" value={pForm.code} onChange={e => setPForm({ ...pForm, code: e.target.value })} placeholder="e.g. PRD-01" /></div>
            <div className="form-group"><label>Name</label><input className="form-control" value={pForm.name} onChange={e => setPForm({ ...pForm, name: e.target.value })} /></div>
            <div className="form-group"><label>HSN Code</label><input className="form-control" value={pForm.hsn_code} onChange={e => setPForm({ ...pForm, hsn_code: e.target.value })} /></div>
            <div className="form-group"><label>Unit</label>
              <select className="form-control" value={pForm.unit} onChange={e => setPForm({ ...pForm, unit: e.target.value })}>
                <option>Ltr</option><option>Kg</option><option>Pcs</option>
              </select>
            </div>
            <div className="form-group"><label>Tax %</label><input type="number" className="form-control" value={pForm.tax_per} onChange={e => setPForm({ ...pForm, tax_per: e.target.value })} /></div>
            <div className="form-group"><label>Rate (₹)</label><input type="number" className="form-control" value={pForm.rate} onChange={e => setPForm({ ...pForm, rate: e.target.value })} /></div>
            <div className="form-group"><label>Type</label>
              <select className="form-control" value={pForm.type} onChange={e => setPForm({ ...pForm, type: e.target.value })}>
                <option value="Lubricant">Lubricant</option><option value="Fuel">Fuel</option>
              </select>
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={saveProduct}>💾 Save</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {showForm && tab === 'Pumps' && (
        <div className="section-card">
          <div className="section-header">Add Pump</div>
          <div className="form-row">
            <div className="form-group"><label>Pump No (Leave blank to auto-generate)</label><input className="form-control" value={puForm.pump_no} onChange={e => setPuForm({ ...puForm, pump_no: e.target.value })} placeholder="e.g. P-01" /></div>
            <div className="form-group"><label>Pump Name</label><input className="form-control" value={puForm.name} onChange={e => setPuForm({ ...puForm, name: e.target.value })} placeholder="Road Side Pump 3" /></div>
            <div className="form-group"><label>Nozzles</label><input type="number" className="form-control" value={puForm.nozzles} onChange={e => setPuForm({ ...puForm, nozzles: e.target.value })} min={1} max={8} /></div>
            <div className="form-group"><label>Primary Product</label>
              <select className="form-control" value={puForm.product_code} onChange={e => setPuForm({ ...puForm, product_code: e.target.value })}>
                <option value="">-- Select Product --</option>
                {(products.data || []).map(p => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
              </select>
            </div>
            <div className="form-group"><label>Status</label>
              <select className="form-control" value={puForm.status} onChange={e => setPuForm({ ...puForm, status: e.target.value })}>
                <option>Active</option><option>Inactive</option>
              </select>
            </div>

          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={savePump}>💾 Save</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {showForm && tab === 'Tanks' && (
        <div className="section-card">
          <div className="section-header">Add Tank</div>
          <div className="form-row">
            <div className="form-group"><label>Tank No (Leave blank to auto-generate)</label><input className="form-control" value={tForm.tank_no} onChange={e => setTForm({ ...tForm, tank_no: e.target.value })} placeholder="e.g. T-01" /></div>
            <div className="form-group"><label>Product</label>
              <select className="form-control" value={tForm.product_code} onChange={e => setTForm({ ...tForm, product_code: e.target.value })}>
                <option value="MS">MS</option><option value="HSD">HSD</option><option value="CNG">CNG</option>
              </select>
            </div>
            <div className="form-group"><label>Capacity (L)</label><input type="number" className="form-control" value={tForm.capacity} onChange={e => setTForm({ ...tForm, capacity: e.target.value })} /></div>
            <div className="form-group"><label>Current Stock (L)</label><input type="number" className="form-control" value={tForm.current_stock} onChange={e => setTForm({ ...tForm, current_stock: e.target.value })} /></div>
            <div className="form-group"><label>Min Level (L)</label><input type="number" className="form-control" value={tForm.min_level} onChange={e => setTForm({ ...tForm, min_level: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={saveTank}>💾 Save</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {showForm && tab === 'Nozzles' && (
        <div className="section-card">
          <div className="section-header">{editRow ? 'Edit Nozzle Mapping' : 'Add Nozzle Mapping'}</div>
          <div className="form-row">
            <div className="form-group"><label>Nozzle Name/No. (Leave blank to auto-generate)</label><input className="form-control" value={noForm.nozzle_no} onChange={e => setNoForm({ ...noForm, nozzle_no: e.target.value })} placeholder="e.g. N-01" /></div>
            <div className="form-group"><label>Pump</label>
              <select className="form-control" value={noForm.pump_id} onChange={e => setNoForm({ ...noForm, pump_id: e.target.value })}>
                <option value="">-- Select Pump --</option>
                {(pumps.data || []).map(p => <option key={p.id} value={p.id}>{p.pump_no} ({p.name})</option>)}
              </select>
            </div>
            <div className="form-group"><label>Tank (Fuel Source)</label>
              <select className="form-control" value={noForm.tank_id} onChange={e => setNoForm({ ...noForm, tank_id: e.target.value })}>
                <option value="">-- Select Tank --</option>
                {(tanks.data || []).filter(t => Number(t.is_active ?? 1) === 1).map(t => <option key={t.id} value={t.id}>{t.tank_no} — {t.product_code}</option>)}
              </select>
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={saveNozzle}>💾 Save Nozzle</button>
            <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditRow(null); setNoForm(EMPTY_NO); }}>✕ Cancel</button>
          </div>
        </div>
      )}

      {showForm && tab === 'Customers' && (
        <div className="section-card">
          <div className="section-header">Add Customer</div>
          <div className="form-row">
            <div className="form-group"><label>Name</label><input className="form-control" value={cForm.name} onChange={e => setCForm({ ...cForm, name: e.target.value })} /></div>
            <div className="form-group"><label>GSTIN</label><input className="form-control" value={cForm.gstin} onChange={e => setCForm({ ...cForm, gstin: e.target.value })} /></div>
            <div className="form-group"><label>Type</label>
              <select className="form-control" value={cForm.type} onChange={e => setCForm({ ...cForm, type: e.target.value })}>
                <option>Cash</option><option>Credit</option>
              </select>
            </div>
            <div className="form-group"><label>Credit Limit (₹)</label><input type="number" className="form-control" value={cForm.credit_limit} onChange={e => setCForm({ ...cForm, credit_limit: e.target.value })} /></div>
            <div className="form-group"><label>Phone</label><input className="form-control" value={cForm.phone} onChange={e => setCForm({ ...cForm, phone: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={saveCustomer}>💾 Save</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {showForm && tab === 'Suppliers' && (
        <div className="section-card">
          <div className="section-header">Add Supplier</div>
          <div className="form-row">
            <div className="form-group"><label>Name</label><input className="form-control" value={sForm.name} onChange={e => setSForm({ ...sForm, name: e.target.value })} /></div>
            <div className="form-group"><label>GSTIN</label><input className="form-control" value={sForm.gstin} onChange={e => setSForm({ ...sForm, gstin: e.target.value })} /></div>
            <div className="form-group"><label>Contact</label><input className="form-control" value={sForm.contact} onChange={e => setSForm({ ...sForm, contact: e.target.value })} /></div>
            <div className="form-group"><label>Products Supplied</label><input className="form-control" value={sForm.product} onChange={e => setSForm({ ...sForm, product: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={saveSupplier}>💾 Save</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {/* ════════════ INLINE EDIT FORMS ════════════ */}
      {editRow && tab === 'Products' && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ Edit Product — {editRow.code}</div>
          <div className="form-row">
            <div className="form-group"><label>Name</label><input className="form-control" value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} /></div>
            <div className="form-group"><label>HSN Code</label><input className="form-control" value={editRow.hsn_code || ''} onChange={e => setEditRow({ ...editRow, hsn_code: e.target.value })} /></div>
            <div className="form-group"><label>Unit</label>
              <select className="form-control" value={editRow.unit} onChange={e => setEditRow({ ...editRow, unit: e.target.value })}>
                <option>Ltr</option><option>Kg</option><option>Pcs</option>
              </select>
            </div>
            <div className="form-group"><label>Tax %</label><input type="number" className="form-control" value={editRow.tax_per} onChange={e => setEditRow({ ...editRow, tax_per: e.target.value })} /></div>
            <div className="form-group"><label>Rate (₹) *</label><input type="number" className="form-control" value={editRow.rate} onChange={e => setEditRow({ ...editRow, rate: e.target.value })} /></div>
            <div className="form-group"><label>Rate Effective From</label><input type="date" className="form-control" value={editRow.effective_date} onChange={e => setEditRow({ ...editRow, effective_date: e.target.value })} /></div>
            <div className="form-group"><label>Rate Remarks</label><input className="form-control" value={editRow.remarks} placeholder="e.g. Price hike" onChange={e => setEditRow({ ...editRow, remarks: e.target.value })} /></div>
            <div className="form-group"><label>Type</label>
              <select className="form-control" value={editRow.type} onChange={e => setEditRow({ ...editRow, type: e.target.value })}>
                <option value="Lubricant">Lubricant</option><option value="Fuel">Fuel</option>
              </select>
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={updateProduct}>💾 Update</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {editRow && tab === 'Pumps' && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ Edit Pump — {editRow.pump_no}</div>
          <div className="form-row">
            <div className="form-group"><label>Pump Name</label><input className="form-control" value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} /></div>
            <div className="form-group"><label>Nozzles</label><input type="number" className="form-control" value={editRow.nozzles} onChange={e => setEditRow({ ...editRow, nozzles: e.target.value })} min={1} max={8} /></div>
            <div className="form-group"><label>Primary Product</label>
              <select className="form-control" value={editRow.product_code} onChange={e => setEditRow({ ...editRow, product_code: e.target.value })}>
                <option value="">-- Select Product --</option>
                {(products.data || []).map(p => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
              </select>
            </div>
            <div className="form-group"><label>Status</label>
              <select className="form-control" value={editRow.status} onChange={e => setEditRow({ ...editRow, status: e.target.value })}>
                <option>Active</option><option>Inactive</option>
              </select>
            </div>

            <div className="form-group"><label>Last Reading</label><input type="number" className="form-control" value={editRow.last_reading} onChange={e => setEditRow({ ...editRow, last_reading: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={updatePump}>💾 Update</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {editRow && tab === 'Tanks' && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ Edit Tank — {editRow.tank_no}</div>
          <div className="form-row">
            <div className="form-group"><label>Product</label>
              <select className="form-control" value={editRow.product_code} onChange={e => setEditRow({ ...editRow, product_code: e.target.value })}>
                <option value="MS">MS</option><option value="HSD">HSD</option><option value="CNG">CNG</option>
              </select>
            </div>
            <div className="form-group"><label>Capacity (L)</label><input type="number" className="form-control" value={editRow.capacity} onChange={e => setEditRow({ ...editRow, capacity: e.target.value })} /></div>
            <div className="form-group"><label>Current Stock (L)</label><input type="number" className="form-control" value={editRow.current_stock} onChange={e => setEditRow({ ...editRow, current_stock: e.target.value })} /></div>
            <div className="form-group"><label>Min Level (L)</label><input type="number" className="form-control" value={editRow.min_level} onChange={e => setEditRow({ ...editRow, min_level: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={updateTank}>💾 Update</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {editRow && tab === 'Customers' && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ Edit Customer — {editRow.name}</div>
          <div className="form-row">
            <div className="form-group"><label>Name</label><input className="form-control" value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} /></div>
            <div className="form-group"><label>GSTIN</label><input className="form-control" value={editRow.gstin || ''} onChange={e => setEditRow({ ...editRow, gstin: e.target.value })} /></div>
            <div className="form-group"><label>Type</label>
              <select className="form-control" value={editRow.type} onChange={e => setEditRow({ ...editRow, type: e.target.value })}>
                <option>Cash</option><option>Credit</option>
              </select>
            </div>
            <div className="form-group"><label>Credit Limit (₹)</label><input type="number" className="form-control" value={editRow.credit_limit} onChange={e => setEditRow({ ...editRow, credit_limit: e.target.value })} /></div>
            <div className="form-group"><label>Phone</label><input className="form-control" value={editRow.phone || ''} onChange={e => setEditRow({ ...editRow, phone: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={updateCustomer}>💾 Update</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {editRow && tab === 'Suppliers' && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ Edit Supplier — {editRow.name}</div>
          <div className="form-row">
            <div className="form-group"><label>Name</label><input className="form-control" value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} /></div>
            <div className="form-group"><label>GSTIN</label><input className="form-control" value={editRow.gstin || ''} onChange={e => setEditRow({ ...editRow, gstin: e.target.value })} /></div>
            <div className="form-group"><label>Contact</label><input className="form-control" value={editRow.contact || ''} onChange={e => setEditRow({ ...editRow, contact: e.target.value })} /></div>
            <div className="form-group"><label>Products Supplied</label><input className="form-control" value={editRow.product || ''} onChange={e => setEditRow({ ...editRow, product: e.target.value })} /></div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={updateSupplier}>💾 Update</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {/* ════════════ PRODUCTS TABLE ════════════ */}
      {!current.loading && tab === 'Products' && (
        <div className="section-card">
          <div className="section-header">Product Master ({(products.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>Code</th>
                <th style={{ textAlign: 'left' }}>Product Name</th><th style={{ textAlign: 'left' }}>HSN</th>
                <th style={{ textAlign: 'left' }}>Unit</th><th style={{ textAlign: 'left' }}>Type</th><th>Tax %</th><th>Rate (₹)</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {(products.data || []).map((p, i) => (
                  <tr key={p.id}>
                    <td>{i + 1}</td>
                    <td><span className="badge badge-primary">{p.code}</span></td>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td style={{ fontFamily: 'monospace' }}>{p.hsn_code}</td>
                    <td>{p.unit}</td>
                    <td><span className={`badge ${p.type === 'Fuel' ? 'badge-danger' : 'badge-success'}`}>{p.type}</span></td>
                    <td className="right-align">{p.tax_per}%</td>
                    <td className="right-align">
                      ₹{fmtNum(p.rate)}
                      <button style={{ ...btnEdit, marginLeft: 8, padding: '2px 5px', fontSize: 10 }} title="Rate History" onClick={() => setHistoryCode(p.code)}>🕒</button>
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Can module="products" action="update">
                        <button style={btnEdit} title="Edit" onClick={() => openEdit(p)}>✏️</button>
                      </Can>
                      <Can module="products" action="delete">
                        <button style={btnDel} title="Delete" onClick={() => openDel(p, 'Products')}>🗑️</button>
                      </Can>                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ PUMPS TABLE ════════════ */}
      {!current.loading && tab === 'Pumps' && (
        <div className="section-card">
          <div className="section-header">Pump Master ({(pumps.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th>#</th><th style={{ textAlign: 'left' }}>Pump No</th><th style={{ textAlign: 'left' }}>Name</th>
                <th style={{ textAlign: 'left' }}>Product</th>
                <th style={{ textAlign: 'right' }}>Nozzles</th><th style={{ textAlign: 'right' }}>Last Reading</th><th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>

              </tr></thead>
              <tbody>
                {(pumps.data || []).map((p, i) => (
                  <tr key={p.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{p.pump_no}</td>
                    <td>{p.name}</td>
                    <td><span className="badge badge-info">{p.product_code}</span></td>
                    <td className="right-align">{p.nozzles}</td>
                    <td className="right-align">{fmtNum(p.last_reading)}</td>

                    <td style={{ textAlign: 'center' }}>
                      <button className={`badge ${p.status === 'Active' ? 'badge-success' : 'badge-danger'}`}
                        style={{ border: 'none', cursor: 'pointer', outline: 'none' }}
                        onClick={() => togglePumpStatus(p)}>
                        {p.status}
                      </button>
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Can module="pumps" action="update">
                        <button style={btnEdit} title="Edit" onClick={() => openEdit(p)}>✏️</button>
                      </Can>

                      <Can module="pumps" action="delete">
                        {Number(p.has_transactions) ? (
                          <span title="This record is linked to existing transactions and cannot be deleted. Please mark it as Inactive instead." style={{ opacity: 0.45, marginLeft: 4, cursor: 'not-allowed' }}>🔒</span>
                        ) : (
                          <button style={btnDel} title="Delete" onClick={() => openDel(p, 'Pumps')}>🗑️</button>
                        )}
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ NOZZLES TABLE ════════════ */}
      {!current.loading && tab === 'Nozzles' && (
        <div className="section-card">
          <div className="section-header">Nozzle Master ({(nozzles.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>#</th>
                  <th style={{ textAlign: 'left' }}>Nozzle No</th>
                  <th style={{ textAlign: 'left' }}>Pump</th>
                  <th style={{ textAlign: 'left' }}>Tank (Source)</th>
                  <th style={{ textAlign: 'left' }}>Product</th>
                  <th style={{ textAlign: 'right' }}>Current Rate</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(nozzles.data || []).length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No nozzles mapped yet.</td></tr>
                ) : (nozzles.data || []).map((n, i) => (
                  <tr key={n.id} style={Number(n.is_active ?? 1) ? undefined : { opacity: 0.5 }}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)' }}>{n.nozzle_no}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{n.pump_no}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{n.pump_name}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{n.tank_no}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{n.product_name}</div>
                    </td>
                    <td>
                      <span className={`badge ${n.product_code === 'MS' ? 'badge-danger' : n.product_code === 'HSD' ? 'badge-info' : 'badge-success'}`}>
                        {n.product_code}
                      </span>
                    </td>
                    <td className="right-align">
                      ₹{fmtNum(n.product_rate || 0)} / {n.unit || 'Ltr'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Can module="pumps" action="update" fallback={<span className={`badge ${Number(n.is_active ?? 1) ? 'badge-success' : 'badge-danger'}`}>{Number(n.is_active ?? 1) ? 'Active' : 'Inactive'}</span>}>
                        <button className={`badge ${Number(n.is_active ?? 1) ? 'badge-success' : 'badge-danger'}`}
                          style={{ border: 'none', cursor: 'pointer', outline: 'none' }}
                          title="Toggle Active / Inactive"
                          onClick={() => toggleNozzleStatus(n)}>
                          {Number(n.is_active ?? 1) ? 'Active' : 'Inactive'}
                        </button>
                      </Can>
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Can module="pumps" action="update">
                        <button style={btnEdit} title="Edit" onClick={() => openEdit(n)}>✏️</button>
                      </Can>

                      <Can module="pumps" action="delete">
                        {Number(n.has_transactions) ? (
                          <span title="This record is linked to existing transactions and cannot be deleted. Please mark it as Inactive instead." style={{ opacity: 0.45, marginLeft: 4, cursor: 'not-allowed' }}>🔒</span>
                        ) : (
                          <button style={btnDel} title="Delete" onClick={() => setConfirmNozzle(n)}>🗑️</button>
                        )}
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ TANKS TABLE ════════════ */}
      {!current.loading && tab === 'Tanks' && (
        <>
        <div className="section-card">
          <div className="section-header">Tank Master ({(tanks.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>Tank No</th>
                <th style={{ textAlign: 'left' }}>Product</th><th>Capacity (L)</th>
                <th>Current Stock (L)</th><th>Min Level (L)</th><th>Fill %</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {(tanks.data || []).map((t, i) => {
                  const pct = Math.round((Number(t.current_stock) / Number(t.capacity)) * 100) || 0;
                  return (
                    <tr key={t.id} style={Number(t.is_active ?? 1) ? undefined : { opacity: 0.5 }}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 700, color: 'var(--primary)' }}>{t.tank_no}</td>
                      <td><span className={`badge ${t.product_code === 'MS' ? 'badge-danger' : t.product_code === 'CNG' ? 'badge-success' : 'badge-info'}`}>{t.product_code}</span></td>
                      <td className="right-align">{fmtNum(t.capacity)}</td>
                      <td className="right-align">{fmtNum(t.current_stock)}</td>
                      <td className="right-align">{fmtNum(t.min_level)}</td>
                      <td className="right-align">
                        <div className="mini-progress"><div style={{ width: `${pct}%`, background: pct < 20 ? 'var(--danger)' : 'var(--secondary)' }} /></div>
                        {pct}%
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <Can module="tanks" action="update" fallback={<span className={`badge ${Number(t.is_active ?? 1) ? 'badge-success' : 'badge-danger'}`}>{Number(t.is_active ?? 1) ? 'Active' : 'Inactive'}</span>}>
                          <button className={`badge ${Number(t.is_active ?? 1) ? 'badge-success' : 'badge-danger'}`}
                            style={{ border: 'none', cursor: 'pointer', outline: 'none' }}
                            title="Toggle Active / Inactive"
                            onClick={() => toggleTankStatus(t)}>
                            {Number(t.is_active ?? 1) ? 'Active' : 'Inactive'}
                          </button>
                        </Can>
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <Can module="tanks" action="update">
                          <button style={btnEdit} title="Edit" onClick={() => openEdit(t)}>✏️</button>
                        </Can>

                        <Can module="tanks" action="delete">
                          {Number(t.has_transactions) ? (
                            <span title="This record is linked to existing transactions and cannot be deleted. Please mark it as Inactive instead." style={{ opacity: 0.45, marginLeft: 4, cursor: 'not-allowed' }}>🔒</span>
                          ) : (
                            <button style={btnDel} title="Delete" onClick={() => openDel(t, 'Tanks')}>🗑️</button>
                          )}
                        </Can>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <TankLedger tanks={tanks.data} />
        </>
      )}

      {/* ════════════ CUSTOMERS TABLE ════════════ */}
      {!current.loading && tab === 'Customers' && (
        <div className="section-card">
          <div className="section-header">Customer Master ({(customers.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>Name</th>
                <th style={{ textAlign: 'left' }}>GSTIN</th><th>Credit Limit</th>
                <th>Balance</th><th style={{ textAlign: 'left' }}>Type</th>
                <th style={{ textAlign: 'left' }}>Phone</th><th style={{ textAlign: 'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {(customers.data || []).map((c, i) => (
                  <tr key={c.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '11.5px' }}>{c.gstin || '—'}</td>
                    <td className="right-align">₹{fmtNum(c.credit_limit)}</td>
                    <td className="right-align" style={{ color: Number(c.balance) > 0 ? 'var(--danger)' : 'var(--secondary)' }}>₹{fmtNum(c.balance)}</td>
                    <td><span className={`badge ${c.type === 'Credit' ? 'badge-warning' : 'badge-success'}`}>{c.type}</span></td>
                    <td>{c.phone || '—'}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Can module="customers" action="update">
                        <button style={btnEdit} title="Edit" onClick={() => openEdit(c)}>✏️</button>
                      </Can>

                      <Can module="customers" action="delete">
                        <button style={btnDel} title="Delete" onClick={() => openDel(c, 'Customers')}>🗑️</button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ SUPPLIERS TABLE ════════════ */}
      {!current.loading && tab === 'Suppliers' && (
        <div className="section-card">
          <div className="section-header">Supplier Master ({(suppliers.data || []).length})</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>Name</th>
                <th style={{ textAlign: 'left' }}>GSTIN</th><th style={{ textAlign: 'left' }}>Contact</th>
                <th style={{ textAlign: 'left' }}>Products</th><th style={{ textAlign: 'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {(suppliers.data || []).map((s, i) => (
                  <tr key={s.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '11.5px' }}>{s.gstin}</td>
                    <td>{s.contact}</td>
                    <td><span className="badge badge-info">{s.product}</span></td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <Can module="suppliers" action="update">
                        <button style={btnEdit} title="Edit" onClick={() => openEdit(s)}>✏️</button>
                      </Can>

                      <Can module="suppliers" action="delete">
                        <button style={btnDel} title="Delete" onClick={() => openDel(s, 'Suppliers')}>🗑️</button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════════ EMPLOYEES TABLE ════════════ */}
      {!current.loading && tab === 'Employees' && (
        <div className="section-card">
          <div className="section-header">Employee Master</div>
          {showForm && (
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
              <div className="form-row">
                <div className="form-group"><label>Name</label><input className="form-control" id="emp_name" placeholder="Full Name" /></div>
                <div className="form-group"><label>Role</label>
                  <select className="form-control" id="emp_role">
                    <option>Manager</option><option>Attendant</option><option>Cashier</option>
                    <option>Security</option><option>Mechanic</option><option>Supervisor</option>
                  </select>
                </div>
                <div className="form-group"><label>Phone</label><input className="form-control" id="emp_phone" placeholder="10-digit" /></div>
                <div className="form-group"><label>Join Date</label><input type="date" className="form-control" id="emp_date" /></div>
                <div className="form-group"><label>Salary (₹)</label><input type="number" className="form-control" id="emp_salary" placeholder="0" /></div>
              </div>
              <div className="action-bar" style={{ padding: '0 0 10px' }}>
                <button className="btn btn-primary" onClick={() => {
                  const name = document.getElementById('emp_name').value;
                  const role = document.getElementById('emp_role').value;
                  const phone = document.getElementById('emp_phone').value;
                  const date = document.getElementById('emp_date').value;
                  const salary = document.getElementById('emp_salary').value;
                  if (!name || !phone) { notify('❌ Name and Phone required'); return; }
                  setEmployees(prev => [...prev, { id: Date.now(), name, role, phone, join_date: date, salary: parseFloat(salary) || 0, status: 'Active' }]);
                  notify('✅ Employee saved'); setShowForm(false);
                }}>💾 Save</button>
                <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button>
              </div>
            </div>
          )}
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>Name</th>
                <th style={{ textAlign: 'left' }}>Role</th><th style={{ textAlign: 'left' }}>Phone</th>
                <th style={{ textAlign: 'left' }}>Join Date</th><th>Salary (₹)</th>
                <th style={{ textAlign: 'left' }}>Status</th><th style={{ textAlign: 'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {employees.map((e, i) => (
                  <tr key={e.id}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{e.name}</td>
                    <td><span className="badge badge-info">{e.role}</span></td>
                    <td>{e.phone}</td>
                    <td>{e.join_date || '—'}</td>
                    <td className="right-align">₹{fmtNum(e.salary)}</td>
                    <td><span className="badge badge-success">{e.status}</span></td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button style={btnDel} onClick={() => setEmployees(prev => prev.filter(x => x.id !== e.id))}>🗑️</button>
                    </td>
                  </tr>
                ))}
                {employees.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No employees added yet. Click "Add New" to get started.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmNozzle}
        title="Delete Nozzle Mapping"
        message={`Delete nozzle mapping for ${confirmNozzle?.nozzle_no}? Historical readings will remain, but this nozzle will no longer appear in future shifts.`}
        onConfirm={delNozzle}
        onCancel={() => setConfirmNozzle(null)}
      />

      <ConfirmModal
        isOpen={!!confirm}
        title={`Delete ${confirm?.entity?.replace(/s$/, '') || 'Record'}`}
        message={`Delete "${confirm?.row?.name || confirm?.row?.code || confirm?.row?.tank_no || confirm?.row?.pump_no}"? (Admin only) The record will be soft-deleted and hidden from all views.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirm(null)}
      />

      {historyCode && (
        <RateHistoryModal
          code={historyCode}
          onClose={() => setHistoryCode(null)}
          onDeleteClick={(rate) => setConfirmDeleteRate(rate)}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmDeleteRate}
        title="Delete Rate Entry?"
        message={confirmDeleteRate
          ? `Delete rate ₹${confirmDeleteRate.rate} effective ${confirmDeleteRate.effective_date}? This cannot be undone.`
          : ''}
        onConfirm={async () => {
          try {
            await api.productRates.remove(confirmDeleteRate.id);
            notify('✅ Rate entry deleted');
            setConfirmDeleteRate(null);
            setHistoryCode(null);
            products.reload();
          } catch (e) { notify('❌ ' + e.message); }
        }}
        onCancel={() => setConfirmDeleteRate(null)}
        confirmLabel="Delete"
      />
    </div>
  );
}

function RateHistoryModal({ code, onClose, onDeleteClick }) {
  const { data, loading } = useApi(() => api.productRates.history(code), [code]);

  return (
    <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content" style={{ background: 'white', padding: 20, borderRadius: 8, width: '90%', maxWidth: 600, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'between', alignItems: 'center', marginBottom: 15, borderBottom: '1px solid #eee', paddingBottom: 10 }}>
          <h3 style={{ margin: 0 }}>Rate History — {code}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', marginLeft: 'auto' }}>✕</button>
        </div>
        {loading ? <Spinner /> : (
          <div style={{ overflowY: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Date</th>
                  <th style={{ textAlign: 'right' }}>Rate</th>
                  <th style={{ textAlign: 'right' }}>Tax %</th>
                  <th style={{ textAlign: 'left' }}>Remarks</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(data || []).map(r => (
                  <tr key={r.id}>
                    <td style={{ textAlign: 'left', fontWeight: 600 }}>{r.effective_date}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>
                      ₹ {Number(r.rate).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {Number(r.tax_per).toFixed(2)}%
                    </td>
                    <td style={{ textAlign: 'left', fontSize: 12 }}>{r.remarks || '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <button style={{ ...btnDel, padding: '2px 5px' }} onClick={() => onDeleteClick(r)}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const btnEdit = { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };
const btnDel = { background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
